// RECIPEAI1-F1 (DEC-593) and MAPCREATE1 (DEC-594): the "Add these N items to your inventory?" approval step, shared by the AI recipe
// import and the Map ingredients step of a franchisor recipe. Nothing is created until the person ticks an item here: every box starts
// unticked, one per item, and the server refuses a create whose names are not the approved list.
//
// Draws what it is handed and keeps no state of its own: the caller owns the ticked set and re-draws on a change. Text goes in with
// textContent only (names come from a file or a franchisor). Rows are 44px touch targets. No new CSS: the ii-* card classes.

export interface CreateCandidate {
  /** The caller's own key for this ingredient. */
  key: string;
  name: string;
  /** How the unticked state reads for this item, for example "2 lines will be left out". Empty for none. */
  leftOutNote: string;
}

export interface CreateConfirmOptions {
  candidates: readonly CreateCandidate[];
  /** The keys ticked so far. Starts empty every time the step is shown. */
  approved: ReadonlySet<string>;
  busy: boolean;
  testidPrefix: string;
  /** The one-line reason under the title. */
  intro: string;
  /** The primary button's label for `count` ticked items. */
  acceptLabel: (count: number) => string;
  /** The primary button is disabled for this reason (a sentence shown under the list), or null when it may be pressed. */
  blockedReason?: (approvedCount: number) => string | null;
  /** True when an unticked item is simply left out (the import), false when every item must be ticked (the map). */
  showLeftOut: boolean;
  classes?: { primary: string; secondary: string; actions: string };
  onToggle: (key: string, ticked: boolean) => void;
  onBack: () => void;
  onAccept: () => void;
}

const DEFAULT_CLASSES = {
  primary: "ii-primary",
  secondary: "ii-secondary",
  actions: "ii-row ii-actions",
};

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string | undefined,
  text: string | undefined,
  testid: string,
): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);
  if (cls !== undefined) made.className = cls;
  if (text !== undefined) made.textContent = text;
  if (testid !== "") made.dataset["testid"] = testid;
  return made;
}

/** The title: "Add this item to your inventory?" or "Add these 3 items to your inventory?". */
export function createConfirmTitle(count: number): string {
  return `Add ${count === 1 ? "this item" : `these ${count} items`} to your inventory?`;
}

/** The primary button for the import: "Add 3 items and import", or "Import without adding items" when none is ticked. */
export function importAcceptLabel(count: number): string {
  return count === 0
    ? "Import without adding items"
    : `Add ${count} ${count === 1 ? "item" : "items"} and import`;
}

/** The primary button for a map: "Add 3 items and save". */
export function mapAcceptLabel(count: number): string {
  return `Add ${count} ${count === 1 ? "item" : "items"} and save`;
}

/** The names left unticked, worded as the import words them: "Orgeat (2 lines will be left out)". */
export function leftOutLine(left: readonly CreateCandidate[]): string {
  return `Not added: ${left
    .map((candidate) =>
      candidate.leftOutNote === ""
        ? candidate.name
        : `${candidate.name} (${candidate.leftOutNote})`,
    )
    .join(", ")}`;
}

export function buildCreateConfirm(options: CreateConfirmOptions): {
  card: HTMLElement;
  actions: HTMLElement;
} {
  const classes = options.classes ?? DEFAULT_CLASSES;
  const prefix = options.testidPrefix;
  const approvedCount = options.candidates.filter((candidate) =>
    options.approved.has(candidate.key),
  ).length;
  const card = node("div", "ii-card", undefined, prefix);
  card.append(
    node("div", "ii-title", createConfirmTitle(options.candidates.length), `${prefix}-title`),
    node("div", "ii-tert", options.intro, ""),
  );
  const list = node("ul", undefined, undefined, "");
  list.setAttribute("aria-label", "Items to add");
  list.style.listStyle = "none";
  list.style.padding = "0";
  list.style.margin = "0";
  for (const candidate of options.candidates) {
    const box = node("input", undefined, undefined, `${prefix}-check`);
    box.type = "checkbox";
    box.setAttribute("aria-label", `Add ${candidate.name}`);
    box.checked = options.approved.has(candidate.key);
    box.addEventListener("change", () => options.onToggle(candidate.key, box.checked));
    const row = node("label", undefined, undefined, `${prefix}-item`);
    row.append(box, ` ${candidate.name}`);
    row.style.display = "flex";
    row.style.alignItems = "center";
    row.style.gap = "8px";
    row.style.minHeight = "44px";
    const entry = node("li", undefined, undefined, "");
    entry.append(row);
    list.append(entry);
  }
  card.append(list);
  if (options.showLeftOut) {
    const left = options.candidates.filter((candidate) => !options.approved.has(candidate.key));
    if (left.length > 0)
      card.append(node("div", "ii-tert", leftOutLine(left), `${prefix}-left-out`));
  }
  const blocked = options.blockedReason?.(approvedCount) ?? null;
  if (blocked !== null) card.append(node("div", "ii-tert", blocked, `${prefix}-blocked`));
  const actions = node("div", classes.actions, undefined, "");
  const back = node("button", classes.secondary, "Back", `${prefix}-back`);
  back.type = "button";
  back.disabled = options.busy;
  back.addEventListener("click", options.onBack);
  const accept = node(
    "button",
    classes.primary,
    options.acceptLabel(approvedCount),
    `${prefix}-accept`,
  );
  accept.type = "button";
  accept.disabled = options.busy || blocked !== null;
  accept.addEventListener("click", options.onAccept);
  actions.append(back);
  if (classes.actions.includes("ii-row")) actions.append(node("span", "ii-sp", undefined, ""));
  actions.append(accept);
  return { card, actions };
}
